class CustomHeroSlider {
  constructor(container) {
    this.container = container;
    this.slides = container.querySelectorAll('.custom-hero-slider__slide');
    this.dots = container.querySelectorAll('.custom-hero-slider__dot');
    this.prevBtn = container.querySelector('.custom-hero-slider__nav--prev');
    this.nextBtn = container.querySelector('.custom-hero-slider__nav--next');
    this.currentSlide = 0;
    this.autoplayInterval = null;
    this.isTransitioning = false;
    
    // Get settings from section
    this.getSettings();
    
    if (this.slides.length > 1) {
      this.init();
    }
  }

  getSettings() {
    const section = this.container.closest('[data-section-id]');
    if (!section) return;

    // Try to get settings from window object (set by theme)
    const sectionId = section.dataset.sectionId;
    const sectionSettings = window.themeSettings?.[sectionId];
    
    this.autoplay = sectionSettings?.autoplay ?? false;
    this.autoplaySpeed = (sectionSettings?.autoplay_speed ?? 5) * 1000;
    this.sliderHeightDesktop = sectionSettings?.slider_height_desktop ?? 700;
    this.sliderHeightMobile = sectionSettings?.slider_height_mobile ?? 600;

    // Apply heights
    this.applyHeights();
  }

  applyHeights() {
    const wrapper = this.container.querySelector('.custom-hero-slider__wrapper');
    if (wrapper) {
      wrapper.style.setProperty('--slider-height-desktop', `${this.sliderHeightDesktop}px`);
      wrapper.style.setProperty('--slider-height-mobile', `${this.sliderHeightMobile}px`);
    }
  }

  init() {
    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Touch events for swipe
    this.initTouchEvents();

    // Autoplay
    if (this.autoplay) {
      this.startAutoplay();
      
      // Pause on hover
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());
      
      // Pause when tab is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else if (this.autoplay) {
          this.startAutoplay();
        }
      });
    }

    // Pause videos when slide is not active
    this.handleVideoPlayback();
    
    // Initialize adjacent slides for peek effect
    this.initializeAdjacentSlides();
    
    // Initialize dots colors for first slide
    this.updateDotsColors(this.currentSlide);
  }
  
  initializeAdjacentSlides() {
    if (this.slides.length < 2) return;
    
    // Mark all slides as hidden initially
    this.slides.forEach((slide, i) => {
      slide.classList.remove('is-hidden', 'is-prev', 'is-next');
      
      const shouldBeVisible = 
        i === this.currentSlide || // active slide
        (this.currentSlide > 0 && i === this.currentSlide - 1) || // prev slide
        (this.currentSlide < this.slides.length - 1 && i === this.currentSlide + 1); // next slide
      
      if (!shouldBeVisible) {
        slide.classList.add('is-hidden');
      }
    });
    
    // Only show prev if not at start
    if (this.currentSlide > 0) {
      const prevIndex = this.currentSlide - 1;
      this.slides[prevIndex].classList.add('is-prev');
    }
    
    // Only show next if not at end
    if (this.currentSlide < this.slides.length - 1) {
      const nextIndex = this.currentSlide + 1;
      this.slides[nextIndex].classList.add('is-next');
    }
    
    // Update navigation buttons state
    this.updateNavButtons();
  }

  goToSlide(index, direction = 'next') {
    if (this.isTransitioning || index === this.currentSlide) return;
    if (index < 0 || index >= this.slides.length) return; // Don't go beyond bounds
    
    this.isTransitioning = true;
    
    const oldIndex = this.currentSlide;
    
    // Remove all state classes from all slides
    this.slides.forEach((slide, i) => {
      slide.classList.remove('is-active', 'is-prev', 'is-next', 'is-hidden');
      
      // Mark all slides as hidden by default
      const shouldBeVisible = 
        i === index || // active slide
        (index > 0 && i === index - 1) || // prev slide
        (index < this.slides.length - 1 && i === index + 1); // next slide
      
      if (!shouldBeVisible) {
        slide.classList.add('is-hidden');
      }
    });
    
    // Set active slide
    this.slides[index].classList.add('is-active');
    
    // Set up adjacent slides for peek effect (non-infinite)
    if (index > 0) {
      this.slides[index - 1].classList.add('is-prev');
    }
    if (index < this.slides.length - 1) {
      this.slides[index + 1].classList.add('is-next');
    }
    
    // Update dots
    this.dots[oldIndex]?.classList.remove('is-active');
    this.dots[index]?.classList.add('is-active');

    // Update dots colors based on current slide
    this.updateDotsColors(index);

    // Update current slide
    this.currentSlide = index;

    // Handle video playback
    this.handleVideoPlayback();
    
    // Update navigation buttons
    this.updateNavButtons();

    // Reset transition lock
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);

    // Restart autoplay
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  nextSlide() {
    if (this.currentSlide < this.slides.length - 1) {
      this.goToSlide(this.currentSlide + 1, 'next');
    }
  }

  prevSlide() {
    if (this.currentSlide > 0) {
      this.goToSlide(this.currentSlide - 1, 'prev');
    }
  }
  
  updateDotsColors(index) {
    const currentSlide = this.slides[index];
    if (!currentSlide) return;
    
    const dotsActiveColor = currentSlide.dataset.dotsActiveColor;
    const dotsInactiveColor = currentSlide.dataset.dotsInactiveColor;
    
    // Update CSS variables for dots colors
    if (dotsActiveColor || dotsInactiveColor) {
      this.container.style.setProperty('--dots-active-color', dotsActiveColor || '');
      this.container.style.setProperty('--dots-inactive-color', dotsInactiveColor || '');
    }
  }
  
  updateNavButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    // Disable prev button if at start
    if (this.currentSlide === 0) {
      this.prevBtn.disabled = true;
    } else {
      this.prevBtn.disabled = false;
    }
    
    // Disable next button if at end
    if (this.currentSlide === this.slides.length - 1) {
      this.nextBtn.disabled = true;
    } else {
      this.nextBtn.disabled = false;
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.slides.length > 1) {
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, this.autoplaySpeed);
    }
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  handleKeyboard(e) {
    if (e.key === 'ArrowLeft') {
      this.prevSlide();
    } else if (e.key === 'ArrowRight') {
      this.nextSlide();
    }
  }

  initTouchEvents() {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    this.container.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.container.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX, minSwipeDistance);
    }, { passive: true });
  }

  handleSwipe(startX, endX, minDistance) {
    const diff = startX - endX;
    
    if (Math.abs(diff) > minDistance) {
      if (diff > 0) {
        // Swiped left - next slide
        this.nextSlide();
      } else {
        // Swiped right - previous slide
        this.prevSlide();
      }
    }
  }

  handleVideoPlayback() {
    // Pause all videos first
    this.slides.forEach((slide) => {
      const videos = slide.querySelectorAll('video');
      videos.forEach(video => {
        video.pause();
      });
    });

    // Play video in active slide
    const activeSlide = this.slides[this.currentSlide];
    const activeVideos = activeSlide.querySelectorAll('video');
    activeVideos.forEach(video => {
      video.play().catch(error => {
        // Autoplay was prevented, which is fine
        console.log('Video autoplay prevented:', error);
      });
    });
  }

  destroy() {
    this.stopAutoplay();
    
    // Remove event listeners would go here
    // For simplicity, we're not implementing full cleanup
  }
}

// Initialize all sliders on the page
function initCustomHeroSliders() {
  const sliders = document.querySelectorAll('.custom-hero-slider');
  
  sliders.forEach(slider => {
    // Check if already initialized
    if (!slider.dataset.initialized) {
      new CustomHeroSlider(slider);
      slider.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomHeroSliders);
} else {
  initCustomHeroSliders();
}

// Re-initialize when Shopify section is loaded/reloaded (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.custom-hero-slider')) {
    initCustomHeroSliders();
  }
});

// Clean up when section is unloaded in theme editor
document.addEventListener('shopify:section:unload', (event) => {
  const slider = event.target.querySelector('.custom-hero-slider');
  if (slider && slider.sliderInstance) {
    slider.sliderInstance.destroy();
  }
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomHeroSlider;
}
